import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, User, Heart, ShoppingBag, Menu, X, Mic } from 'lucide-react';
import { useStore } from '../context/StoreContext';
import { useLocale } from '../context/LocaleContext';
import AuthModal from './AuthModal';
import LocaleSwitcher from './LocaleSwitcher';
import { absMediaUrl } from './ProductCard';

const LOGO_SRC = '/api/files/feronstore/brand/b917f5ca85b544458ebf6869329c79ff.webp';

const OfferStrip = () => {
  const { t, formatPrice, freeShipThresholdINR } = useLocale();
  const items = [
    t('offer1', { price: formatPrice(freeShipThresholdINR) }),
    t('offer2'),
    t('offer3'),
    t('offer4')
  ];
  return (
    <div className="bg-[#111] text-white text-[11px] tracking-wider py-1.5 overflow-hidden">
      <div className="flex gap-16 animate-[scroll_30s_linear_infinite] whitespace-nowrap">
        {[...items, ...items].map((text, i) => <span key={`${text}-${i}`} className="px-4">{text}</span>)}
      </div>
      <style>{`@keyframes scroll {0%{transform:translateX(0)}100%{transform:translateX(-50%)}}`}</style>
    </div>
  );
};

const NAV_STRUCTURE = {
  MEN: {
    key: 'men',
    sections: {
      topwear: ['Oversized T-Shirts', 'T-Shirts', 'Shirts', 'Hoodies', 'Sweatshirts', 'Jackets', 'Polos'],
    }
  },
  WOMEN: {
    key: 'women',
    sections: {
      topwear: ['Oversized T-Shirts', 'T-Shirts', 'Shirts', 'Hoodies', 'Sweatshirts', 'Dresses', 'Crop Tops'],
    }
  }
};

const MegaMenu = ({ open, data, gender, t }) => {
  if (!open) return null;
  return (
    <div className="absolute left-0 top-full w-full bg-white shadow-xl border-t border-gray-100 z-50">
      <div className="max-w-7xl mx-auto px-8 py-8 grid grid-cols-4 gap-8">
        {Object.entries(data.sections).map(([sectionKey, items]) => (
          <div key={sectionKey}>
            <h4 className="font-bold text-[13px] tracking-wider text-[#333] mb-3 border-b-2 border-[#22c55e] inline-block pb-1">{t(sectionKey)}</h4>
            <ul className="space-y-2 mt-2">
              {items.map((it) => (
                <li key={it}>
                  <Link to={`/shop/${gender.toLowerCase()}/${it.toLowerCase().replace(/ /g, '-')}`} className="text-[13px] text-gray-700 hover:text-[#22c55e] transition-colors">{it}</Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
};

const Header = () => {
  const [hoverMenu, setHoverMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [query, setQuery] = useState('');
  const { cartCount, wishlist, user } = useStore();
  const { t } = useLocale();
  const navigate = useNavigate();

  const onSearch = (e) => {
    e.preventDefault();
    if (query.trim()) navigate(`/search?q=${encodeURIComponent(query)}`);
  };

  return (
    <>
      <OfferStrip />
      <header className="sticky top-0 z-40 bg-white border-b border-gray-100" onMouseLeave={() => setHoverMenu(null)}>
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-[70px] flex items-center justify-between gap-2">
          <div className="flex items-center gap-6 flex-1 min-w-0">
            <button className="md:hidden" onClick={() => setMobileOpen(true)}><Menu size={22} /></button>
            <nav className="hidden md:flex items-center gap-7 h-full">
              {Object.entries(NAV_STRUCTURE).map(([k, data]) => (
                <div key={k} className="h-[70px] flex items-center" onMouseEnter={() => setHoverMenu(k)}>
                  <Link to={`/shop/${k.toLowerCase()}`} className={`text-[14px] font-bold tracking-wider transition-colors ${hoverMenu === k ? 'text-[#22c55e]' : 'text-[#222]'}`}>{t(data.key)}</Link>
                </div>
              ))}
            </nav>
          </div>

          <Link to="/" className="shrink-0 flex items-center justify-center px-1">
            <img src={absMediaUrl(LOGO_SRC)} alt="Feron" width={120} height={120} className="h-9 md:h-16 w-auto object-contain" loading="eager" fetchpriority="high" decoding="async" />
          </Link>

          <div className="flex items-center gap-2 md:gap-3 flex-1 min-w-0 justify-end">
            <form onSubmit={onSearch} className="hidden lg:flex items-center bg-[#f6f6f6] rounded-full px-4 h-10 w-[260px]">
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('searchPlaceholder')} className="bg-transparent outline-none text-sm flex-1" />
              <Mic size={16} className="text-gray-400 mr-2" />
              <button type="submit"><Search size={18} className="text-gray-600" /></button>
            </form>
            <LocaleSwitcher />
            <button onClick={() => setAuthOpen(true)} className="flex flex-col items-center text-[10px] text-gray-600 hover:text-[#22c55e] transition-colors">
              <User size={20} />
              <span className="hidden md:block">{user ? user.name : t('account')}</span>
            </button>
            <Link to="/wishlist" className="flex flex-col items-center text-[10px] text-gray-600 hover:text-[#22c55e] transition-colors relative">
              <Heart size={20} />
              <span className="hidden md:block">{t('wishlist')}</span>
              {wishlist.length > 0 && <span className="absolute -top-1 -right-2 bg-[#22c55e] text-white text-[9px] rounded-full w-4 h-4 flex items-center justify-center">{wishlist.length}</span>}
            </Link>
            <Link to="/cart" className="flex flex-col items-center text-[10px] text-gray-600 hover:text-[#22c55e] transition-colors relative">
              <ShoppingBag size={20} />
              <span className="hidden md:block">{t('bag')}</span>
              {cartCount > 0 && <span className="absolute -top-1 -right-2 bg-[#22c55e] text-white text-[9px] rounded-full w-4 h-4 flex items-center justify-center">{cartCount}</span>}
            </Link>
          </div>
        </div>

        {hoverMenu && <MegaMenu open data={NAV_STRUCTURE[hoverMenu]} gender={hoverMenu} t={t} />}

        {mobileOpen && (
          <div className="fixed inset-0 bg-black/50 z-50 md:hidden" onClick={() => setMobileOpen(false)}>
            <div className="bg-white w-72 h-full p-4 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-4">
                <span className="font-black text-lg">{t('brandName')}</span>
                <button onClick={() => setMobileOpen(false)}><X /></button>
              </div>
              {Object.entries(NAV_STRUCTURE).map(([k, data]) => (
                <div key={k} className="mb-5">
                  <div className="font-bold text-sm text-[#22c55e] mb-2">{t(data.key)}</div>
                  {Object.entries(data.sections).map(([secKey, items]) => (
                    <div key={secKey} className="pl-3 mb-3">
                      <div className="text-xs font-semibold text-gray-500 mb-1">{t(secKey)}</div>
                      {items.map((it) => (
                        <Link key={it} to={`/shop/${k.toLowerCase()}/${it.toLowerCase().replace(/ /g, '-')}`} className="block text-sm py-1 text-gray-700" onClick={() => setMobileOpen(false)}>{it}</Link>
                      ))}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}
      </header>
      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} />
    </>
  );
};

export default Header;
